import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { Card } from '../primitives/Card'
import { Badge } from '../primitives/Badge'
import { Icon } from '../primitives/Icon'

export type HalfDaySlot = 'AM' | 'PM'

/** Map of `YYYY-MM-DD` → 'AM' | 'PM'. Absent keys mean "FULL". */
export type HalfDayMap = Readonly<Record<string, HalfDaySlot>>

interface HalfDayPickerProps {
  /** Inclusive start date `YYYY-MM-DD`. */
  startDate: string
  /** Inclusive end date `YYYY-MM-DD`. */
  endDate: string
  /** ISO dates of public holidays in scope. Excluded from the picker. */
  publicHolidays: ReadonlyArray<string>
  value: HalfDayMap
  onChange: (next: HalfDayMap) => void
  /** ISO dates the remaining balance can't cover as a full day — the FULL
   *  button is disabled for these, so only AM / PM are selectable. The caller
   *  is responsible for pre-seeding these dates with a half in `value`. */
  requiredHalfDates?: ReadonlyArray<string>
}

export function listWorkingDays(
  start: string,
  end: string,
  holidays: ReadonlyArray<string>,
): string[] {
  const out: string[] = []
  const cur = new Date(`${start}T00:00:00Z`)
  const last = new Date(`${end}T00:00:00Z`)
  const phSet = new Set(holidays)
  while (cur <= last) {
    const dow = cur.getUTCDay() // 0 Sun, 6 Sat
    const iso = cur.toISOString().slice(0, 10)
    if (dow !== 0 && dow !== 6 && !phSet.has(iso)) out.push(iso)
    cur.setUTCDate(cur.getUTCDate() + 1)
  }
  return out
}

function weekdayLabel(iso: string): string {
  return format(parseISO(iso), 'EEE')
}

function shortDate(iso: string): string {
  return format(parseISO(iso), 'dd MMM')
}

const SLOT_OPTIONS: ReadonlyArray<{ key: 'FULL' | HalfDaySlot; label: string }> = [
  { key: 'FULL', label: 'Full' },
  { key: 'AM', label: 'AM' },
  { key: 'PM', label: 'PM' },
]

/**
 * Per-day FULL / AM / PM picker. Renders one row per working day in
 * `[startDate, endDate]`, skipping weekends and supplied public
 * holidays. Controlled — callers own the `HalfDayMap` state.
 */
export function HalfDayPicker({
  startDate,
  endDate,
  publicHolidays,
  value,
  onChange,
  requiredHalfDates,
}: HalfDayPickerProps): JSX.Element | null {
  const days = listWorkingDays(startDate, endDate, publicHolidays)
  const requiredHalf = new Set(requiredHalfDates ?? [])
  const halfCount = days.filter(
    (d) => value[d] === 'AM' || value[d] === 'PM',
  ).length
  // Default expansion follows the range length, recomputed EACH render:
  // short requests (≤ 3 working days) open expanded — that's where per-day
  // tweaking is most likely and the rows are cheap — while longer ranges stay
  // collapsed. Any existing half-day (e.g. a restored draft) also opens it.
  // Deriving this per render (rather than a useState initializer) matters
  // because the calendar seeds a 1-day pick before the user extends the range:
  // a frozen initial value would leave a 6-day request stuck expanded.
  // `override` holds a manual toggle and wins over the derived default until
  // the user toggles again. Hooks must run before the early return below.
  const autoExpanded = halfCount > 0 || days.length <= 3
  const [override, setOverride] = useState<boolean | null>(null)
  const expanded = override ?? autoExpanded
  if (days.length === 0) return null

  function set(date: string, slot: 'FULL' | HalfDaySlot): void {
    const next: Record<string, HalfDaySlot> = { ...value }
    if (slot === 'FULL') {
      delete next[date]
    } else {
      next[date] = slot
    }
    onChange(next)
  }

  return (
    <Card
      padding="12px 14px"
      style={{ background: 'var(--info-bg)', borderColor: 'var(--info-line)' }}
    >
      <button
        type="button"
        data-testid="half-day-toggle"
        onClick={() => setOverride(!expanded)}
        aria-expanded={expanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 10,
          marginBottom: 8,
          padding: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--info-fg)',
          }}
        >
          Day-by-day options
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {halfCount > 0 ? (
            <Badge variant="info">
              {halfCount} half-day{halfCount === 1 ? '' : 's'}
            </Badge>
          ) : null}
          <Icon
            name="expand_more"
            size={18}
            color="var(--info-fg)"
            aria-hidden="true"
            style={{
              transition: 'transform .15s',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </span>
      </button>
      <p
        style={{
          fontSize: 11,
          color: 'var(--info-fg)',
          opacity: 0.85,
          marginBottom: expanded ? 10 : 0,
        }}
      >
        {expanded
          ? 'Default is a full day. Click AM or PM to take a half day.'
          : 'Default is a full day. Expand to make any date a half day (AM or PM).'}
      </p>
      {expanded ? (
      <div
        data-testid="half-day-rows"
        style={{ display: 'grid', gap: 4 }}
      >
        {days.map((iso) => {
          const slot = value[iso] ?? 'FULL'
          const mustHalf = requiredHalf.has(iso)
          return (
            <div
              key={iso}
              data-testid="half-day-row"
              data-date={iso}
              data-slot={slot}
              data-must-half={mustHalf ? 'true' : 'false'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '6px 10px',
                borderRadius: 8,
                background:
                  slot === 'FULL' ? 'var(--surface)' : 'var(--primary-bg)',
                border: '1px solid var(--french-gray-2)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text)',
                    minWidth: 70,
                  }}
                >
                  {shortDate(iso)}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {weekdayLabel(iso)}
                </span>
                {mustHalf ? (
                  <span
                    style={{ fontSize: 10.5, color: 'var(--warning-fg)', fontWeight: 600 }}
                  >
                    half day only
                  </span>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {SLOT_OPTIONS.map((opt) => {
                  const active = slot === opt.key
                  // A day the balance can't cover as full can't be taken FULL —
                  // disable that option so only AM / PM remain.
                  const disabled = mustHalf && opt.key === 'FULL'
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      data-testid={`half-day-btn-${opt.key.toLowerCase()}`}
                      data-active={active ? 'true' : 'false'}
                      disabled={disabled}
                      title={disabled ? 'Only half a day of balance remains' : undefined}
                      onClick={() => set(iso, opt.key)}
                      style={{
                        padding: '4px 10px',
                        minHeight: 32,
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.4 : 1,
                        border: active
                          ? '2px solid var(--steel-blue)'
                          : '1px solid var(--border)',
                        background: active
                          ? opt.key === 'FULL'
                            ? 'var(--steel-blue)'
                            : 'var(--primary-bg)'
                          : 'var(--surface)',
                        color: active && opt.key === 'FULL' ? 'var(--surface)' : 'var(--text)',
                      }}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      ) : null}
    </Card>
  )
}
